/* eslint-disable */
import {Injectable} from '@angular/core';
import {Observable, Observer} from 'rxjs';
import {Order, PagingRequest, QueryResult, ResourceQueryResult, Services} from '../../util/services';
import {EmptyMessage} from '../../util/messages';
import {List, Map, Set} from 'immutable';
import {OffsetDateTime} from '../../util/dates';
import {SmsTemplateResource, SmsTemplateResourceService} from './sms-template-resource.service';
import {MultiselectOptionItem} from "../../../util/core-utils";

/* eslint-enable */

@Injectable()
export class SmsTemplateService implements SmsTemplate.Service {

  constructor(private resourceService: SmsTemplateResourceService) {
  }

  query(request: SmsTemplate.QueryRequest): Observable<QueryResult<SmsTemplate.SmsTemplate>> {
    return Observable.create((observer: Observer<QueryResult<SmsTemplate.SmsTemplate>>) => {
      const resourceRequest: SmsTemplateResource.QueryRequest = {
        id: Services.createIdParameter(request.id),
        external_id: request.externalId,
        name: request.name,
        disabled: request.disabled,
        q: request.queryText,
        order: Services.createOrderFieldParameter(Keys.toOrderFieldKey, request.orders),
        page_number: request.paging ? request.paging.pageNumber : undefined,
        number_of_items: request.paging ? request.paging.numberOfItems : undefined,
        no_progress_bar: request.noProgressBar,
      };
      return this.resourceService.query(resourceRequest).subscribe(
        (result: ResourceQueryResult<SmsTemplateResource.SmsTemplate>) => {
          observer.next({
            items: this.toPublicList(result.items),
            pagingResult: result.pagingResult
          });
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  get(request: SmsTemplate.GetRequest): Observable<SmsTemplate.SmsTemplate> {
    return Observable.create((observer: Observer<SmsTemplate.SmsTemplate>) => {
      const resourceRequest: SmsTemplateResource.GetRequest = {
        id: request.id
      };
      return this.resourceService.get(resourceRequest).subscribe(
        (result: SmsTemplateResource.SmsTemplate) => {
          observer.next(this.toPublic(result));
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  create(request: SmsTemplate.CreateRequest): Observable<EmptyMessage> {
    return Observable.create((observer: Observer<EmptyMessage>) => {
      const resourceRequest: SmsTemplateResource.CreateRequest = {
        external_id: request.externalId,
        name: request.name,
        template: request.template,
      };
      return this.resourceService.create(resourceRequest).subscribe(
        (result: EmptyMessage) => {
          observer.next(result);
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  update(request: SmsTemplate.UpdateRequest): Observable<EmptyMessage> {
    return Observable.create((observer: Observer<EmptyMessage>) => {
      const resourceRequest: SmsTemplateResource.UpdateRequest = {
        id: request.id,
        external_id: request.externalId,
        name: request.name,
        template: request.template,
      };
      return this.resourceService.update(resourceRequest).subscribe(
        (result: EmptyMessage) => {
          observer.next(result);
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  setDisabled(request: SmsTemplate.DisableRequest): Observable<EmptyMessage> {
    return Observable.create((observer: Observer<EmptyMessage>) => {
      const resourceRequest: SmsTemplateResource.DisableRequest = {
        id: request.id,
        disabled: request.disabled
      };
      return this.resourceService.disable(resourceRequest).subscribe(
        (result: EmptyMessage) => {
          observer.next(result);
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  private toPublicList(resourceList: SmsTemplateResource.SmsTemplate[]): List<SmsTemplate.SmsTemplate> {
    return List.of(...resourceList.map((r) => this.toPublic(r)));
  }

  private toPublic(r: SmsTemplateResource.SmsTemplate): SmsTemplate.SmsTemplate {
    return {
      id: r.id,
      externalId: r.external_id,
      name: r.name,
      template: r.template,
      creationTime: Services.toOffsetDateTime(r.creation_time),
      updateTime: Services.toOffsetDateTime(r.update_time),
      disabled: r.disabled
    };
  }

}


export namespace SmsTemplate {

  export interface Service {

    // <editor-fold desc="CRUD">

    query(request: QueryRequest): Observable<QueryResult<SmsTemplate>>;

    get(request: GetRequest): Observable<EmptyMessage>;

    create(request: CreateRequest): Observable<EmptyMessage>;

    update(request: UpdateRequest): Observable<EmptyMessage>;

    setDisabled(request: DisableRequest): Observable<EmptyMessage>;

    // </editor-fold>

  }

  export interface SmsTemplate {
    id: number;
    externalId: string;
    name: string;
    template: string;
    disabled: boolean;
    creationTime: OffsetDateTime;
    updateTime: OffsetDateTime;
  }

  export interface QueryRequest {
    id?: Set<number>;
    externalId?: string;
    name?: string;
    queryText?: string;
    orders?: Set<Order<OrderField>>;
    paging?: PagingRequest;
    disabled?: boolean;
    noProgressBar?: boolean;
  }

  export interface GetRequest {
    id: number;
  }

  export interface CreateRequest {
    externalId?: string;
    name: string;
    template: string;
  }

  export interface UpdateRequest {
    id: number;
    externalId: string;
    name: string;
    template: string;
  }

  export interface DisableRequest {
    id: number;
    disabled: boolean;
  }

  export enum SmsIntegrationType {
    BIP = 'BIP',
    FUTAR = 'FUTAR'
  }

  export const integrationTypes: MultiselectOptionItem<SmsIntegrationType>[] = [
    {id: SmsIntegrationType.BIP, itemName: 'SMS_INTEGRATION_TYPE_BIP'},
    {id: SmsIntegrationType.FUTAR, itemName: 'SMS_INTEGRATION_TYPE_FUTAR'}
  ]

  export enum OrderField {
    ID,
    EXTERNAL_ID,
    NAME,
    CREATION_TIME,
    UPDATE_TIME
  }

  export enum ValidatedField {
    UNKNOWN,

    EXTERNAL_ID,
    NAME,
    SUBJECT
  }

}

class Keys {

  private static readonly ID = 'id';
  private static readonly EXTERNAL_ID = 'external_id';
  private static readonly NAME = 'name';
  private static readonly CREATION_TIME = 'creation_time';
  private static readonly UPDATE_TIME = 'update_time';

  private static readonly orderFieldKeyMap: Map<SmsTemplate.OrderField, string> = Map.of(
    SmsTemplate.OrderField.ID, Keys.ID,
    SmsTemplate.OrderField.EXTERNAL_ID, Keys.EXTERNAL_ID,
    SmsTemplate.OrderField.NAME, Keys.NAME,
    SmsTemplate.OrderField.CREATION_TIME, Keys.CREATION_TIME,
    SmsTemplate.OrderField.UPDATE_TIME, Keys.UPDATE_TIME,
  );

  private static readonly keyValidatedFieldMap: Map<string, SmsTemplate.ValidatedField> = Map.of(
    SmsTemplate.ValidatedField.EXTERNAL_ID, Keys.EXTERNAL_ID,
    SmsTemplate.ValidatedField.NAME, Keys.NAME,
  );

  public static toOrderFieldKey(field: SmsTemplate.OrderField): string {
    return Keys.orderFieldKeyMap.get(field)!;
  }

  public static toValidatedField(fieldKey: string): SmsTemplate.ValidatedField {
    return Keys.keyValidatedFieldMap.get(fieldKey, SmsTemplate.ValidatedField.UNKNOWN);
  }

}
